import { FC, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Switch,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { AsyncFieldWrapper, SnackbarContainer } from 'components/standalone';
import { CAN_EDIT_CONTRACTOR_DETAILS } from 'providers/permissions';
import { PermissionsProvider } from 'providers/permissionsProvider';

import { ResourceTypeTable } from '../components';
import { ContractorContainer, RegionOption } from '../containers';

const formatNumber = (value: number | null | undefined): string =>
  value != null ? String(value) : '—';

const ContractorsShowInner: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();
  const {
    selectedContractor,
    isLoading,
    contractorLoaded,
    contractorError,
    contractorRegions,
    contractorRegionsLoaded,
    loadContractorById,
    loadContractorRegionsForContractor,
    loadRegionOptions,
    resetLoadedFlags,
  } = ContractorContainer.useContainer();

  const version = useVersion();
  const contractorId = selectedContractor?.id;

  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [regionOptionsLoaded, setRegionOptionsLoaded] = useState(false);

  useEffect(() => {
    if (!id) return;
    loadContractorById(id);
  }, [id, version, loadContractorById]);

  useEffect(() => {
    if (!contractorId) return;
    resetLoadedFlags();
    loadContractorRegionsForContractor(contractorId);
  }, [contractorId, resetLoadedFlags, loadContractorRegionsForContractor]);

  useEffect(() => {
    let cancelled = false;
    loadRegionOptions('').then(options => {
      if (cancelled) return;
      setRegionOptions(options);
      setRegionOptionsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loadRegionOptions, version]);

  useEffect(() => {
    if (contractorError || (contractorLoaded && !selectedContractor)) {
      notify('Contractor not found', 'error');
      history.push('/contractor');
    }
  }, [contractorError, contractorLoaded, selectedContractor, notify, history]);

  const regionsById = useMemo(
    () => new Map(regionOptions.map(o => [o.id, o.name])),
    [regionOptions],
  );

  const regionChipNames = useMemo(
    () =>
      contractorRegions
        .map(cr => regionsById.get(cr.regionId))
        .filter((name): name is string => !!name),
    [contractorRegions, regionsById],
  );

  const regionsLoading =
    !!contractorId && (!contractorRegionsLoaded || !regionOptionsLoaded);

  const canEdit = hasPermission(CAN_EDIT_CONTRACTOR_DETAILS);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedContractor) {
    return null;
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.toolbar}>
        {canEdit && (
          <Button
            color="primary"
            variant="text"
            data-cy="contractorShow-editButton"
            onClick={() => history.push(`/contractor/${id}`)}
          >
            Edit
          </Button>
        )}
      </Box>

      <Box className={classes.body}>
        <Box className={classes.row}>
          <Field label="Name" value={selectedContractor.name} />
          <Field label="Corporation" value={selectedContractor.corporation} />
        </Box>

        <Box className={classes.row}>
          <Field
            label="Payment Type"
            value={selectedContractor.paymentType ?? '—'}
          />
          <Field
            label="Payment Rate"
            value={formatNumber(selectedContractor.paymentRate)}
          />
          <Field
            label="Bumper Payment Type"
            value={selectedContractor.bumperPaymentType ?? 'N/A'}
          />
          <Field
            label="Bumper Payment Rate"
            value={formatNumber(selectedContractor.bumperPaymentRate)}
          />
        </Box>

        <Box className={classes.section}>
          <Typography variant="caption" color="textSecondary">
            Regions
          </Typography>
          <AsyncFieldWrapper loading={regionsLoading}>
            <Box className={classes.chipRow}>
              {regionChipNames.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  —
                </Typography>
              ) : (
                regionChipNames.map(name => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    className={classes.chip}
                  />
                ))
              )}
            </Box>
          </AsyncFieldWrapper>
        </Box>

        {selectedContractor.notes && (
          <Box className={classes.section}>
            <Typography variant="caption" color="textSecondary">
              Notes
            </Typography>
            <Typography variant="body1" className={classes.notes}>
              {selectedContractor.notes}
            </Typography>
          </Box>
        )}

        <Box className={classes.row}>
          <BooleanFlag
            label="Active"
            checked={!!selectedContractor.active}
          />
          <BooleanFlag
            label="Suspended"
            checked={!!selectedContractor.suspended}
          />
          <BooleanFlag
            label="High Ceiling Tasks"
            checked={!!selectedContractor.canPerformHighCeilingTasks}
          />
        </Box>

        <Divider className={classes.divider} />

        <ResourceTypeTable readOnly record={selectedContractor} />
      </Box>
    </Box>
  );
};

interface FieldProps {
  label: string;
  value: string;
}

const Field: FC<FieldProps> = ({ label, value }) => {
  const classes = useStyles();
  return (
    <Box className={classes.field}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
};

interface BooleanFlagProps {
  label: string;
  checked: boolean;
}

const BooleanFlag: FC<BooleanFlagProps> = ({ label, checked }) => {
  const classes = useStyles();

  return (
    <Box className={classes.field}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Box className={classes.flagRow}>
        <Switch checked={checked} disabled color="primary" />
        <Typography variant="body2">{checked ? 'Yes' : 'No'}</Typography>
      </Box>
    </Box>
  );
};

export const ContractorsShow: FC = () => (
  <ContractorContainer.Provider>
    <ContractorsShowInner />
  </ContractorContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(2),
  },
  body: {
    padding: theme.spacing(0, 3, 3, 3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 180,
    flex: '1 1 180px',
  },
  flagRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(0.5),
    minHeight: 32,
  },
  chip: {
    margin: 0,
  },
  notes: {
    whiteSpace: 'pre-wrap',
  },
  divider: {
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
}));

export default ContractorsShow;
